import { StatusCodes } from "http-status-codes";

import api, { getApiErrorMessage } from "./api.service";
import { API_ENDPOINTS } from "../config.js";
import { normalizeTableData } from "../utils/tableNormalizer";



export interface Project {
  id: number;
  project_name: string;
  project_description: string;
  data_type: string;
  file_size?: number;
}

export interface CreateProjectData {
  project_name: string;
  project_description: string;
  data_type: string;
}

export interface File {
  id: string;
  name: string;
  metadata?: Record<string, any>;
  file_role?: string;
}

export interface AnalysisRun {
  id: string;
  name: string;
  status: string;
  current_step?: string;
  created_at: string;
  updated_at: string;
  steps: Array<{
    id: number;
    step_name: string;
    status: string;
    parameters?: Record<string, any>;
    summary: any;
    duration_ms?: number;
    completed_at?: string;
  }>;
}

export interface PrecomputedDemo {
  id: string;
  project_name: string;
  name: string;
  slug: string;
  status: "queued" | "running" | "ready" | "failed";
  current_step?: string;
  error_message?: string;
  task_id?: string;
  created_at: string;
  updated_at: string;
  completed_at?: string;
  run: AnalysisRun;
}

export interface TrajectoryConfiguration {
  cluster_column: string;
  root_column: string;
  root_value: string;
  use_rep: string;
}

const fetchAnalysisRuns = async (projectName: string): Promise<AnalysisRun[]> => {
  const response = await api.get<AnalysisRun[]>(API_ENDPOINTS.ANALYSIS_RUNS_URL(projectName));
  if (response?.status === StatusCodes.OK) return response.data;
  throw new Error(getApiErrorMessage(response, "Failed to load saved runs."));
};

const fetchPrecomputedDemos = async (projectName: string): Promise<PrecomputedDemo[]> => {
  const response = await api.get<PrecomputedDemo[]>(API_ENDPOINTS.PRECOMPUTED_DEMOS_URL(projectName));
  if (response?.status === StatusCodes.OK) return response.data;
  throw new Error(getApiErrorMessage(response, "Failed to load demo results."));
};

const fetchAllPrecomputedDemos = async (): Promise<PrecomputedDemo[]> => {
  const response = await api.get<PrecomputedDemo[]>(API_ENDPOINTS.ALL_PRECOMPUTED_DEMOS_URL);
  if (response?.status === StatusCodes.OK) return response.data;
  throw new Error(getApiErrorMessage(response, "Failed to load demo results."));
};

const createPrecomputedDemo = async (
  projectName: string,
  name: string,
  trajectory: TrajectoryConfiguration,
): Promise<PrecomputedDemo> => {
  const response = await api.post<PrecomputedDemo>(
    API_ENDPOINTS.PRECOMPUTED_DEMOS_URL(projectName),
    {
      name,
      trajectory_cluster_column: trajectory.cluster_column,
      trajectory_root_column: trajectory.root_column,
      trajectory_root_value: trajectory.root_value,
      trajectory_use_rep: trajectory.use_rep,
    },
  );
  if (response?.status === StatusCodes.ACCEPTED) return response.data;
  throw new Error(getApiErrorMessage(response, "Failed to start demo results."));
};


export const downloadFullArtifactCsv = async (
  projectName: string,
  step: string,
  artifactId: string,
) => {
  try {
    const res = await api.get<Blob>(
      API_ENDPOINTS.ARTIFACT_DOWNLOAD_CSV_URL(projectName, stepSlug(step), artifactId),
      undefined,
      { responseType: "blob" }
    );

    if (!res || res.status !== StatusCodes.OK) {
      console.error("Full artifact CSV download failed:", res?.status, res?.data);
      throw new Error(`Download failed (${res?.status ?? "no status"})`);
    }

    const blob = res.data as unknown as Blob;

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${stepSlug(step)}_${projectName}_${artifactId}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (e: any) {
    console.error("Full artifact CSV download exception:", e);
    throw e;
  }
};

export const downloadQcFullCsv = (projectName: string, qcId: string) =>
  downloadFullArtifactCsv(projectName, "Quality Control", qcId);



const fetchProjectCards = async (_auth: {
  accessToken: string;
}): Promise<Project[]> => {
  const response = await api.get<any>(API_ENDPOINTS.GET_PROJECT_CARDS_URL);
  if (response?.status === StatusCodes.OK) {
    return response?.data as Project[];
  }
  return [];
};

const createProject = async (
  _auth: { accessToken: string },
  projectData: CreateProjectData,
): Promise<Project> => {
  const response = await api.post<any>(
    API_ENDPOINTS.CREATE_PROJECT_URL,
    projectData,
  );

  if (
    response?.status === StatusCodes.CREATED ||
    response?.status === StatusCodes.OK
  ) {
    return response?.data as Project;
  }

  // Try to extract more specific error message from response
  // Backend returns errors in different formats, handle them all
  let errorMessage = getApiErrorMessage(response, "Failed to create the project.");

  if (response?.data) {
    // Handle array format: {"project_name": ["error message"]}
    if (
      response.data.project_name &&
      Array.isArray(response.data.project_name)
    ) {
      errorMessage = response.data.project_name[0];
    }
    // Handle object format: {"message": "error message"}
    else if (response.data.message) {
      errorMessage = response.data.message;
    }
    // Handle string format: {"error": "error message"}
    else if (response.data.error) {
      errorMessage = response.data.error;
    }
    // Handle direct string response
    else if (typeof response.data === "string") {
      errorMessage = response.data;
    }
  }

  throw new Error(errorMessage);
};

const updateProject = async (
  _auth: { accessToken: string },
  projectId: number,
  projectData: CreateProjectData,
): Promise<Project> => {
  const response = await api.put<any>(
    API_ENDPOINTS.UPDATE_PROJECT_URL(projectId),
    projectData,
  );

  if (response?.status === StatusCodes.OK) {
    return response?.data as Project;
  }

  throw new Error(getApiErrorMessage(response, "Failed to update the project."));
};

const fetchProjectFiles = async (
  _auth: { accessToken: string },
  projectName: string,
): Promise<File[]> => {
  const response = await api.get<any>(API_ENDPOINTS.GET_FILES_URL(projectName));

  if (response?.status === StatusCodes.OK) {
    return response?.data as File[];
  }

  return [];
};

const uploadProjectFiles = async (
  _auth: { accessToken: string },
  projectName: string,
  files: FileList,
  fileRole: string = "auto",
  importOptions: Record<string, string> = {},
): Promise<void> => {
  const formData = new FormData();
  for (let i = 0; i < files.length; i++) {
    formData.append("files", files[i]);
  }
  formData.append("file_role", fileRole);
  formData.append("import_options", JSON.stringify(importOptions));

  const response = await api.post<any>(
    API_ENDPOINTS.UPLOAD_FILE_URL(projectName),
    formData,
    undefined,
    {
      headers: { "Content-Type": "multipart/form-data" },
    },
  );

  if (
    response?.status !== StatusCodes.CREATED &&
    response?.status !== StatusCodes.OK
  ) {
    throw new Error(getApiErrorMessage(response, "Failed to upload the selected files."));
  }
};

const renameProjectFile = async (
  _auth: { accessToken: string },
  projectName: string,
  fileId: string,
  newName: string,
): Promise<void> => {
  const response = await api.put<any>(
    API_ENDPOINTS.RENAME_FILE_URL(projectName, fileId),
    { new_name: newName },
    undefined,
    {
      headers: { "Content-Type": "application/json" },
    },
  );

  if (response?.status !== StatusCodes.OK) {
    throw new Error(getApiErrorMessage(response, "Failed to rename the file."));
  }
};

const deleteProjectFile = async (
  _auth: { accessToken: string },
  projectName: string,
  fileId: string,
): Promise<void> => {
  const response = await api.delete<any>(
    API_ENDPOINTS.DELETE_FILE_URL(projectName, fileId),
  );

  // Backends may return 200 with confirmation or 204 without content.
  // Both responses mean the file record was deleted successfully.
  if (response?.status !== StatusCodes.OK && response?.status !== StatusCodes.NO_CONTENT) {
    throw new Error(getApiErrorMessage(response, "Failed to delete the file."));
  }
};

const deleteProject = async (
  _auth: { accessToken: string },
  projectId: number,
): Promise<void> => {
  const response = await api.delete<any>(
    API_ENDPOINTS.DELETE_PROJECT_URL(projectId),
  );

  // TODO: Backend should return 204 No Content for successful DELETE operations instead of 200
  // Currently backend returns 200 with soft delete message, which is not standard REST practice
  // Accept both 200 and 204 for now to handle current backend behavior
  // Also it still is in project list after deletion, so does it even work?
  if (
    response?.status !== StatusCodes.NO_CONTENT &&
    response?.status !== StatusCodes.OK
  ) {
    throw new Error(getApiErrorMessage(response, "Failed to delete the project."));
  }
};

const runWorkflowStep = async (
  _auth: { accessToken: string },
  projectName: string,
  payload: any,
): Promise<{ task_id?: string; run_id?: string; [key: string]: any }> => {
  const response = await api.post<any>(
    API_ENDPOINTS.SINGLE_CELL_PROCESS_URL(projectName),
    payload,
    undefined,
    { headers: { "Content-Type": "application/json" } },
  );

  if (
    response?.status === StatusCodes.OK ||
    response?.status === StatusCodes.ACCEPTED ||
    response?.status === StatusCodes.CREATED
  ) {
    return response.data;
  }

  throw new Error(getApiErrorMessage(response, "The analysis could not be started. Please try again."));
};


// Ensure this line is fixed
const pollTaskStatus = async (
  taskId: string,
  auth: { accessToken: string },
  onSuccess: (result: any) => void,
  onError: (error: string) => void,
  // Some annotation providers can take substantially longer under load.
  // Keep polling for up to two hours (2400 x 3 seconds) before giving up.
  retries = 2400,
  interval = 3000,
): Promise<void> => {
  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      const response = await api.get<any>(API_ENDPOINTS.TASK_STATUS_URL(taskId));
      if (!response || response.status < 200 || response.status >= 300) {
        onError(getApiErrorMessage(response, "Could not check the analysis status."));
        return;
      }
      const data = response.data;

      // Handle the different task states
      if (data.state === "SUCCESS") {
        const result = data.result;
        if (typeof result === "object") {
          if (result.status === "error") {
            onError(result.message || "Task completed with errors.");
          } else if (result.tableData) {
            // Handle tableData response (new format)
            const td = result.tableData;
            const { rows, columns } = normalizeTableData(td);

            // Handle UMAP-specific logic
            if (result.currentStep === "Umap") {
              
              //  console.log("result",result);  // Inspect the full object
              // console.log("td", td);  // Inspect the full table data or plotSample

                // Pass the normalized UMAP data to onSuccess
                return onSuccess({
                  status: "success",
                  tableData: rows, // UMAP normalized data
                  tableColumns: columns, // Optional, pass table columns if required
                  genesList: result.genesList,
                  currentStep: result.currentStep,
                  nextStep: result.nextStep,
                  message: "UMAP analysis completed successfully",
                  qcArtifactId: result.qcArtifactId,
                  artifactType: result.artifactType,
                  // qcMeta: td,
                });
  
            } else {
              // Handle other steps (QC or others)
              return onSuccess({
                ...result,
                status: "success",
                tableData: rows,   // Normalized data for QC/other steps
                tableColumns: columns,
                genesList: result.genesList,
                currentStep: result.currentStep,
                nextStep: result.nextStep,
                message: "Analysis completed successfully",
                artifactId: result.artifactId ?? td?.artifactId ?? td?.qcArtifactId,
                qcArtifactId: result.qcArtifactId ?? result.artifactId ?? td?.qcArtifactId ?? td?.artifactId,
                artifactType: result.artifactType ?? td?.artifactType,
                qcMeta: {
                  ...(td && !Array.isArray(td) ? td : {}),
                  artifactId: result.artifactId ?? td?.artifactId ?? td?.qcArtifactId,
                  qcArtifactId: result.qcArtifactId ?? result.artifactId ?? td?.qcArtifactId ?? td?.artifactId,
                },
              });
            }
          } else if (result.status === "success") {
            
            onSuccess(result);
          } else if (result.stdout) {
            try {
              const parsed = JSON.parse(result.stdout);
              onSuccess(parsed);
            } catch (err) {
              console.error("Failed to parse result.stdout:", err);
              onError("Backend returned unreadable data.");
            }
          } else {
            onError("Unexpected response format.");
          }
        } else {
          onError("Unexpected result type.");
        }
        return;
      } else if (data.state === "FAILURE") {
        onError("Task failed: " + (data.result?.message || "Unknown error"));
        return;
      } else if (data.state === "PENDING") {
        console.log(`Task ${taskId} is still pending... (attempt ${attempt + 1}/${retries})`);
      } else {
        console.log(`Unknown task state: ${data.state}. Retrying...`);
      }

      // Do not add one final interval after the last status check.
      if (attempt + 1 < retries) {
        await new Promise((res) => setTimeout(res, interval));
      }
    } catch (err) {
      console.error("Polling error:", err);
      onError("Polling failed. Try again.");
      return;
    }
  }

  // If retries exhausted
  onError("Analysis is taking longer than expected. Please try again or reopen the project to check the saved result.");
};


export const stepSlug = (step: string) => {
  const s = (step || "").toLowerCase();
  if (s === "quality control" || s === "qc") return "qc";
  if (s === "feature selection") return "feature_selection";
  if (s === "umap") return "umap";
  if (s === "normalization") return "normalization";
  if (s === "clustering") return "clustering";
  if (s === "cell type annotation") return "cell_type_annotation";
  if (s === "trajectory analysis" || s === "trajectory") return "trajectory";
  if (s === "gene expression" || s === "gene_expression") return "gene_expression";
  return "qc";
};

export async function fetchRows(
  projectName: string,
  step: string,
  artifactId: string,
  page: number,
  pageSize: number,
  sort?: string,
  order?: "asc" | "desc",
  filters?: any
) {
  const params: any = { page: page + 1, pageSize };
  if (sort) params.sort = sort;
  if (order) params.order = order;
  if (filters && Object.keys(filters).length > 0) params.filters = JSON.stringify(filters);

  const url = API_ENDPOINTS.ARTIFACT_ROWS_URL(projectName, stepSlug(step), artifactId);

  const response = await api.get<any>(url, undefined, { params });

  if (response?.status === StatusCodes.OK) return response.data;
  throw new Error(getApiErrorMessage(response, "Failed to load result rows."));
}



export const qcResample = async (
  _auth: { accessToken: string },
  projectName: string,
  body: {
    step?: string;          // ✅ new (optional)
    artifactId?: string;    // ✅ new (optional)
    qcArtifactId?: string;  // ✅ keep for QC backward compat
    seed: number;
    sample_n?: number;
    selectedGene?: string;  // ✅ optional (umap)
  },
): Promise<{
  step?: string;
  artifactId?: string;
  sampleSeed: number;
  sampleN: number;
  totalCells: number;
  plotSample: any;
  cols?: string[];
}> => {
  const response = await api.post<any>(
    API_ENDPOINTS.QC_RESAMPLE_URL(projectName), // ✅ SAME endpoint
    body,
    undefined,
    { headers: { "Content-Type": "application/json" } },
  );

  if (response?.status === StatusCodes.OK || response?.status === StatusCodes.CREATED) {
    return response.data;
  }

  throw new Error(getApiErrorMessage(response, "Failed to refresh the result preview."));
};



const projectsService = {
  fetchProjectCards,
  createProject,
  updateProject,
  fetchProjectFiles,
  uploadProjectFiles,
  renameProjectFile,
  deleteProjectFile,
  deleteProject,
  runWorkflowStep,
  pollTaskStatus,
  fetchRows,
  qcResample,
  stepSlug,
  fetchAnalysisRuns
  ,fetchPrecomputedDemos
  ,fetchAllPrecomputedDemos
  ,createPrecomputedDemo

};

export default projectsService;
